import { ref, computed } from 'vue'
import { useSearchStore } from '@/components/Search/store/index.ts' // adjust import as needed
import type { TDropDownOption } from '@/components/common/SaiDropDown/index.ce.vue'
import { useI18n } from '@/plugins/i18n'
import type { FilterArrayKeys, ISearchState, SearchFilterOptionWithFilterName } from '@/components/Search/types.ts'
import { STANDARDS_SEARCH_TAB, COLLECTIONS_SEARCH_TAB, UPDATES_SEARCH_TAB, DEFAULT_EXPORT_FIELDS } from '@/components/Search/constants.ts';
import { gaTrackSearchProductLinkClick } from '@/utils/gaHelpers'

export function useSearchActions(
  searchType: typeof COLLECTIONS_SEARCH_TAB | typeof STANDARDS_SEARCH_TAB | typeof  UPDATES_SEARCH_TAB,
  filterKeys: FilterArrayKeys<ISearchState>[]
) {

    
  const searchStore = useSearchStore()
  const i18nInstance: any = useI18n()
  const { t } = i18nInstance
  const isAllSelected = ref(false)


  const runSearch = (facetFilter: boolean) => {
    if (searchType === STANDARDS_SEARCH_TAB) {
      searchStore.searchStandards(facetFilter)
    } else if (searchType === COLLECTIONS_SEARCH_TAB) {
      searchStore.searchCollections(facetFilter)
    } else if (searchType === UPDATES_SEARCH_TAB) {
      searchStore.searchUpdates(facetFilter)
    }
  }

  const removeFilter = (item: SearchFilterOptionWithFilterName) => {
    for (const key of filterKeys) {
      if (item.filterName !== key) continue

      const storeValue = searchStore[key]
      if(key == `${searchType}.facetFilter.dateStart` || key == `${searchType}.facetFilter.dateEnd`) {
        searchStore.setDefaultTimeRangeForUpdatesSearch();
      } else if (Array.isArray(storeValue)) {
        // Array: remove the matching id
        const toRemove = storeValue.find(f => f.id === item.id)
        if (toRemove) {
          searchStore.removeFilter(key, { id: toRemove.id, text: toRemove.text })
          //removing top level ics filter should remove lower level ics filter as well
          if(key === `${searchType}.facetFilter.descriptorField`) {
            searchStore[`${searchType}.facetFilter.descriptorGroup`] = [];
            searchStore[`${searchType}.facetFilter.descriptorSubGroup`] = [];
          }
          else if(key === `${searchType}.facetFilter.descriptorGroup`) {
            searchStore[`${searchType}.facetFilter.descriptorSubGroup`] = [];
          }
        }
      } else if (typeof storeValue === 'string') {
        // String: remove the string value
        searchStore.removeFilter(key, { id: storeValue, text: storeValue })
      }

      break // done after handling the key
    }
    runSearch(true)
  }

  const clearAllFilters = () => {
    for (const key of filterKeys) {
      const value = searchStore[key]
      if( key == `${searchType}.facetFilter.dateStart` || key == `${searchType}.facetFilter.dateEnd`) {
        searchStore.setDefaultTimeRangeForUpdatesSearch();
      } else if (Array.isArray(value)) {
        searchStore[key] = []  // clear array
      } else if (typeof value === 'string') {
        searchStore[key] = ''  // clear string
      } else {
        // fallback for undefined or unexpected types
        searchStore[key] = [] as any
      }
    }
    runSearch(true)
  }

  const clearAllSearchBarFilters = () => {
    searchStore.clearSearchBarFilters(searchType)
  }

  const saveFilters = (updatedFilters: Record<any, SearchFilterOptionWithFilterName[]>) => {
    for (const key of filterKeys) {
      const currentValue = searchStore[key]
      if(key == `${searchType}.facetFilter.dateStart` || key == `${searchType}.facetFilter.dateEnd`) {
        const dateStart = updatedFilters[`${searchType}.facetFilter.dateStart`]?.[0]?.id || null;
        const dateEnd = updatedFilters[`${searchType}.facetFilter.dateEnd`]?.[0]?.id || null;
        searchStore.setDefaultTimeRangeForUpdatesSearch(dateStart, dateEnd);
      } else if (Array.isArray(currentValue)) {
        // store expects array → copy the updated array
        searchStore[key] = [...(updatedFilters[key] || [])]
      } else if (typeof currentValue === 'string') {
        // store expects string → take first item's id or empty string
        searchStore[key] = updatedFilters[key]?.[0]?.id || ''
      } else {
        // fallback → set array
        searchStore[key] = [...(updatedFilters[key] || [])] as any
      }
    }
    runSearch(true)
  }
  
  const getResultActionOptions = (): TDropDownOption[] => {
    return searchStore.getResultActionOptions(searchType)
  }

  const getResultsRecordTotal = () => {
    return searchStore.getResultsRecordTotal(searchType)
  }

  const getResultSummaryText = () => {
    return searchStore.getResultSummaryText(searchType)
  }

  const isResultLoaded = computed(() => {
    return searchStore.isResultLoaded(searchType)
  })

  const handleSelectItem = (payload: { item: any }) => {
    searchStore.toggleSelectItem({ searchType, item: payload.item })
  }

  const handleDocAction = (payload: { action: string; docId: string }) => {
    const activeCollectionId = searchStore[`${searchType}.resultData`]?.activeCollectionId;
    searchStore.performDocAction({
      searchType,
      action: payload.action,
      collectionId: activeCollectionId,
      docIds: [payload.docId],
      isBulkAction: false
    })
  }

  const handleSelectAllItem = () => {
    isAllSelected.value = !isAllSelected.value
    searchStore.selectAllItems({ searchType, selectAll: isAllSelected.value })
  }

  const handleBulkDocAction = (selectedAction: TDropDownOption[]) => {
    const actionIds = selectedAction.map(action => action.id)
    if (!actionIds.length) return

    const selectedDocIds = searchStore[`${searchType}.resultData`].selectedItems?.map((item: any) => item.doc_id)
    if (!selectedDocIds?.length) {
      alert(t('noDocSelected'))
      return
    }

    let message = ''
    let action = ''

    switch (actionIds[0]) {
      case 'add_selected':
        message = t('confirmAddToCollection')
        action = 'addItem'
        break
      case 'rem_selected':
        message = t('confirmRemoveFromCollection')
        action = 'remItem'
        break
      case 'watch_selected':
        message = t('confirmWatchDocument')
        action = 'watchItem'
        break
      case 'unwatch_selected':
        message = t('confirmUnwatchDocument')
        action = 'unWatchItem'
        break
    }

    if (confirm(message)) {
      const activeCollectionId = searchStore[`${searchType}.resultData`]?.activeCollectionId;
      searchStore.performDocAction({
        searchType,
        action,
        collectionId: activeCollectionId,
        docIds: selectedDocIds,
        isBulkAction: true
      })
    }
  }

  const getDocOffset = (rowIndex: number) => {
    const currentPage = searchStore[`${searchType}.result.page`];
    const recordsPerPage = searchStore[`${searchType}.resultData`]?.recordsPerPage;
    const pageOffset = (currentPage - 1) * recordsPerPage;
    return pageOffset + rowIndex;
  }

  const handleExportFieldSelection = (fieldKey: string) => {
    const selectedFields = searchStore[`${searchType}.export.selectedFields`] as string[];
    const index = selectedFields.indexOf(fieldKey);
    if (index > -1) {
        // Field is already selected, remove it
        selectedFields.splice(index, 1);
    } else {
        // Field is not selected, add it
        selectedFields.push(fieldKey);
    }
    searchStore[`${searchType}.export.selectedFields`] = selectedFields;
  }

  const handleExportFormatSelection = (format: string) => {
    searchStore[`${searchType}.export.format`] = format;
  }

  const handleExportSearchResults = () => {
    if (searchType === STANDARDS_SEARCH_TAB) {
      searchStore.exportStandardSearchResults()
    } else if (searchType === COLLECTIONS_SEARCH_TAB) {
      searchStore.exportCollectionSearchResults()
    } else if (searchType === UPDATES_SEARCH_TAB) {
      searchStore.exportUpdatesSearchResults()
    }
  }

  const handleExportClose = () => {
    searchStore[`${searchType}.export.status`] = '';
    searchStore[`${searchType}.export.result`] = {};
    searchStore[`${searchType}.export.selectedFields`] = [...DEFAULT_EXPORT_FIELDS[searchType]];
    searchStore[`${searchType}.export.format`] = 'csv';
  }

  const handleProductLinkClick = (docItem: any, docPosition: number) => {
    if (searchType === STANDARDS_SEARCH_TAB) {
      const searchParams = searchStore.getStandardSearchParams(true, searchStore[`${searchType}.result.page`]);
      gaTrackSearchProductLinkClick(searchType, searchParams, searchStore[`${STANDARDS_SEARCH_TAB}.resultData`], docItem, docPosition+1);
    } else if (searchType === COLLECTIONS_SEARCH_TAB) {
      const searchParams = searchStore.getCollectionSearchParams(true, searchStore[`${searchType}.result.page`]);      
      gaTrackSearchProductLinkClick(searchType, searchParams, searchStore[`${COLLECTIONS_SEARCH_TAB}.resultData`], docItem, docPosition+1);
    }
  }

  return {
    // state
    isAllSelected,
    // actions
    handleSelectItem,
    handleDocAction,
    handleSelectAllItem,
    handleBulkDocAction,
    removeFilter,
    clearAllFilters,
    clearAllSearchBarFilters,
    saveFilters,
    getResultActionOptions,
    getResultsRecordTotal,
    getResultSummaryText,
    getDocOffset,
    isResultLoaded,
    runSearch,
    handleExportFieldSelection,
    handleExportFormatSelection,
    handleExportSearchResults,
    handleExportClose,
    handleProductLinkClick
  }
}
